// Builds renderable geometry for a chunk. Emits only faces exposed to a
// transparent neighbour (face culling), bakes per-vertex ambient occlusion into
// vertex colours, and separates opaque blocks from translucent water.

import * as THREE from 'three';
import { Block, blockDef, isTransparent } from '../sim/blocks';
import { CHUNK_SIZE_X, CHUNK_SIZE_Z, WORLD_HEIGHT } from '../sim/constants';
import { World } from '../sim/world';
import { blockTiles, tileUV, Tile } from './textures';

interface Face {
  normal: [number, number, number];
  corners: [number, number, number][];
}

// Corners are ordered counter-clockwise seen from outside; indices [0,1,2,0,2,3].
const FACES: Record<'px' | 'nx' | 'py' | 'ny' | 'pz' | 'nz', Face> = {
  px: {
    normal: [1, 0, 0],
    corners: [
      [1, 0, 1],
      [1, 0, 0],
      [1, 1, 0],
      [1, 1, 1],
    ],
  },
  nx: {
    normal: [-1, 0, 0],
    corners: [
      [0, 0, 0],
      [0, 0, 1],
      [0, 1, 1],
      [0, 1, 0],
    ],
  },
  py: {
    normal: [0, 1, 0],
    corners: [
      [0, 1, 1],
      [1, 1, 1],
      [1, 1, 0],
      [0, 1, 0],
    ],
  },
  ny: {
    normal: [0, -1, 0],
    corners: [
      [0, 0, 0],
      [1, 0, 0],
      [1, 0, 1],
      [0, 0, 1],
    ],
  },
  pz: {
    normal: [0, 0, 1],
    corners: [
      [0, 0, 1],
      [1, 0, 1],
      [1, 1, 1],
      [0, 1, 1],
    ],
  },
  nz: {
    normal: [0, 0, -1],
    corners: [
      [1, 0, 0],
      [0, 0, 0],
      [0, 1, 0],
      [1, 1, 0],
    ],
  },
};

const AO_BRIGHTNESS = [0.5, 0.7, 0.85, 1.0];

interface MeshData {
  positions: number[];
  normals: number[];
  uvs: number[];
  colors: number[];
  indices: number[];
}

function emptyMesh(): MeshData {
  return { positions: [], normals: [], uvs: [], colors: [], indices: [] };
}

/** AO level 0..3 from the two edge neighbours and the diagonal corner. */
function vertexAO(side1: boolean, side2: boolean, corner: boolean): number {
  if (side1 && side2) return 0;
  return 3 - ((side1 ? 1 : 0) + (side2 ? 1 : 0) + (corner ? 1 : 0));
}

/** Opaque for AO purposes: solid and not transparent. */
function opaque(world: World, x: number, y: number, z: number): boolean {
  const b = world.getBlock(x, y, z);
  return blockDef(b).solid && !isTransparent(b);
}

function tileForFace(block: Block, face: keyof typeof FACES): Tile {
  const tiles = blockTiles(block);
  if (face === 'py') return tiles.top;
  if (face === 'ny') return tiles.bottom;
  return tiles.side;
}

function addFace(
  data: MeshData,
  world: World,
  wx: number,
  wy: number,
  wz: number,
  lx: number,
  lz: number,
  block: Block,
  faceKey: keyof typeof FACES,
): void {
  const face = FACES[faceKey];
  const [nx, ny, nz] = face.normal;
  const [u0, v0, u1, v1] = tileUV(tileForFace(block, faceKey));
  const uvCorners = [
    [u0, v0],
    [u1, v0],
    [u1, v1],
    [u0, v1],
  ];

  const base = data.positions.length / 3;
  const normalAxis = nx !== 0 ? 0 : ny !== 0 ? 1 : 2;
  const aoValues: number[] = [];

  for (let i = 0; i < 4; i++) {
    const [cx, cy, cz] = face.corners[i];
    // Position in chunk-local space.
    data.positions.push(lx + cx, wy + cy, lz + cz);
    data.normals.push(nx, ny, nz);
    data.uvs.push(uvCorners[i][0], uvCorners[i][1]);

    // Ambient occlusion sampling in the layer just outside this face.
    const tangentAxes = [0, 1, 2].filter((a) => a !== normalAxis);
    const cornerCoord = [cx, cy, cz];
    const dir = (axis: number): number => (cornerCoord[axis] === 1 ? 1 : -1);
    const outside = [wx + nx, wy + ny, wz + nz];
    const s1 = [...outside];
    s1[tangentAxes[0]] += dir(tangentAxes[0]);
    const s2 = [...outside];
    s2[tangentAxes[1]] += dir(tangentAxes[1]);
    const cor = [...outside];
    cor[tangentAxes[0]] += dir(tangentAxes[0]);
    cor[tangentAxes[1]] += dir(tangentAxes[1]);

    const ao = vertexAO(
      opaque(world, s1[0], s1[1], s1[2]),
      opaque(world, s2[0], s2[1], s2[2]),
      opaque(world, cor[0], cor[1], cor[2]),
    );
    aoValues.push(ao);
    const b = AO_BRIGHTNESS[ao];
    data.colors.push(b, b, b);
  }

  // Flip the quad's diagonal when AO would otherwise create a seam.
  if (aoValues[0] + aoValues[2] > aoValues[1] + aoValues[3]) {
    data.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  } else {
    data.indices.push(base + 1, base + 2, base + 3, base + 1, base + 3, base);
  }
}

function finalize(data: MeshData): THREE.BufferGeometry | null {
  if (data.positions.length === 0) return null;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(data.positions, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(data.normals, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(data.uvs, 2));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(data.colors, 3));
  geo.setIndex(data.indices);
  geo.computeBoundingSphere();
  return geo;
}

export interface ChunkGeometry {
  solid: THREE.BufferGeometry | null;
  water: THREE.BufferGeometry | null;
}

const NEIGHBOURS: [keyof typeof FACES, number, number, number][] = [
  ['px', 1, 0, 0],
  ['nx', -1, 0, 0],
  ['py', 0, 1, 0],
  ['ny', 0, -1, 0],
  ['pz', 0, 0, 1],
  ['nz', 0, 0, -1],
];

export function buildChunkGeometry(world: World, cx: number, cz: number): ChunkGeometry {
  const solid = emptyMesh();
  const water = emptyMesh();

  for (let lx = 0; lx < CHUNK_SIZE_X; lx++) {
    for (let lz = 0; lz < CHUNK_SIZE_Z; lz++) {
      const wx = cx * CHUNK_SIZE_X + lx;
      const wz = cz * CHUNK_SIZE_Z + lz;
      for (let y = 0; y < WORLD_HEIGHT; y++) {
        const block = world.getBlock(wx, y, wz);
        if (block === Block.Air) continue;
        const isWater = block === Block.Water;
        for (const [faceKey, dx, dy, dz] of NEIGHBOURS) {
          const neighbour = world.getBlock(wx + dx, y + dy, wz + dz);
          if (isWater) {
            // Water only shows faces against air (its own surface).
            if (neighbour === Block.Air) {
              addFace(water, world, wx, y, wz, lx, lz, block, faceKey);
            }
          } else if (isTransparent(neighbour) && neighbour !== block) {
            // Air is transparent, so this also covers faces exposed to air.
            addFace(solid, world, wx, y, wz, lx, lz, block, faceKey);
          }
        }
      }
    }
  }

  return { solid: finalize(solid), water: finalize(water) };
}
